import classNames from 'classnames';
import { ReactChild, useEffect, useState } from 'react';
import { ViewPublicComment } from '@Comments/openapi/client/index';
import { getFormattedDate } from '@Comments/services/timeService';
import { ReplyForm, openLoginScreen } from '../ReplyForm/ReplyForm';
import { useAppContext, useAppDispatcher } from '@Comments/context/AppContext';
import { useLoader } from '@Comments/services/openapiClient';
import './Comment.scss';
import { useLocalStorage } from '@Comments/services/useLocalStorage';

type Props = {
  comment: ViewPublicComment;
  isReply?: boolean;
};
export const Comment = ({ comment, isReply }: Props) => {
  const { env, user, thread, activeReplyForm } = useAppContext();
  const dispatch = useAppDispatcher();
  const [repliesAreExpanded, setRepliesAreExpanded] = useState(false);
  const loader = useLoader();
  const formattedCreationDate = getFormattedDate(
    new Date(comment.creationDate),
  );
  const [likes, setLikes] = useState(comment.likes ? comment.likes : 0);
  const handleReplyAction = (urn: string) => {
    if (user.isLoggedIn) {
      dispatch({ type: 'update-active-reply-form', data: urn });
    } else {
      dispatch({ type: 'update-active-reply-form-after-login', data: urn });
      openLoginScreen();
    }
  };
  const [isCommentLiked, setIsCommentLiked] = useLocalStorage({
    key: `srf-comments-like-${comment.id}`,
    initialValue: false,
  });

  const [isCommentComplained, setIsCommentComplained] = useLocalStorage({
    key: `srf-comments-complaint-${comment.id}`,
    initialValue: false,
  });

  const likeCommentAction = () => {
    if (user.isLoggedIn) {
      setIsCommentLiked(true);
      // fire and forget
      loader.likeComment(comment.urn);
    } else {
      openLoginScreen();
    }
  };

  const handleComplaintAction = () => {
    if (user.isLoggedIn) {
      setIsCommentComplained(true);
      // fire and forget
      loader.saveComplaint(comment.urn);
    } else {
      openLoginScreen();
    }
  };

  // increase like if comment was liked
  // this also runs initially if a comment is set as liked in localStorage
  useEffect(() => {
    if (isCommentLiked) {
      setLikes(likes + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommentLiked]);

  // Function to convert URLs in text to anchor tags
  const linkify = (text: string): ReactChild => {
    const urlRegex = /(\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+)/g;
    return (
      <>
        {text.split(urlRegex).map((part, index) =>
          urlRegex.test(part) ? (
            <a
              key={index}
              href={part.startsWith('www.') ? `https://${part}` : part}
              target="_blank"
              rel="noopener noreferrer nofollow">
              {part}
            </a>
          ) : (
            <span key={index}>{part}</span>
          ),
        )}
      </>
    );
  };

  const renderCommentText = (): ReactChild => {
    if (comment.authorRole === 'ROLE_MODERATOR') {
      return linkify(comment.text);
    } else {
      return <span>{comment.text}</span>;
    }
  };

  return (
    <li
      className={classNames('srf-comments-comment', {
        'srf-comments-comment--reply': isReply,
      })}>
      <cite className="srf-comments-comment__user">
        <span className="h-offscreen">Kommentar von</span>
        <span>{comment.authorName}</span>
        {comment.authorUsername && (
          <span>&nbsp;({comment.authorUsername})</span>
        )}
        {comment.authorRole === 'ROLE_MODERATOR' && (
          <span
            className={`srf-comments-comment__logo srf-comments-comment__logo--${env.bu}`}></span>
        )}
      </cite>
      {formattedCreationDate && (
        <span className="srf-comments-comment__time">
          <time dateTime={formattedCreationDate}>{formattedCreationDate}</time>
        </span>
      )}
      <blockquote className="srf-comments-comment__content">
        {renderCommentText()}
      </blockquote>
      <div className="srf-comments-comment__actions">
        <button
          type="button"
          className={classNames('srf-comments-comment__action', {
            'srf-comments-comment__action--disabled': isCommentLiked,
          })}
          onClick={() => {
            if (!isCommentLiked) {
              likeCommentAction();
            }
          }}>
          Zustimmen
          {likes > 0 && (
            <>
              <span className="h-offscreen">dem Kommentar zustimmen</span>
              <span>
                {' '}
                (<b className="srf-comments-comment__count">{likes}</b>)
              </span>
            </>
          )}
        </button>
        {thread.state === 'open' && !isReply && (
          <button
            type="button"
            className="srf-comments-comment__action srf-comments-comment__action--with-border"
            onClick={() => {
              handleReplyAction(comment.urn);
            }}>
            Antworten
          </button>
        )}

        <button
          type="button"
          className={classNames(
            'srf-comments-comment__action srf-comments-comment__action--with-border',
            {
              'srf-comments-comment__action--disabled': isCommentComplained,
            },
          )}
          onClick={() => {
            handleComplaintAction();
          }}>
          {isCommentComplained ? 'Gemeldet' : 'Melden'}
        </button>
      </div>
      <div className="srf-comments-comment__replies-wrapper">
        {activeReplyForm === comment.urn && (
          <div className="srf-comments-comment__reply-form srf-comments-comment--reply">
            <ReplyForm
              parentUrn={comment.urn}
              threadUrn={env.threadUrn}
              isUserLoggedIn={user.isLoggedIn}
              activeReplyForm={activeReplyForm}
            />
          </div>
        )}
        {comment.replies &&
          comment.replies.length > 0 &&
          !repliesAreExpanded && (
            <div className="srf-comments-comment__replies">
              <button
                type="button"
                className="srf-comments-comment__action srf-comments-comment__action--expand"
                onClick={() => {
                  setRepliesAreExpanded(true);
                }}>
                Antworten anzeigen (
                <b className="srf-comments-comment__count">
                  {comment.replies?.length}
                </b>
                )
              </button>
            </div>
          )}
        {comment.replies &&
          comment.replies.length > 0 &&
          repliesAreExpanded && (
            <ul>
              {comment.replies?.map((comment) => {
                return (
                  <Comment key={comment.id} comment={comment} isReply={true} />
                );
              })}
            </ul>
          )}
      </div>
    </li>
  );
};
